import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";
import strengthLarge from "../../images/strengthLarge.png";
import playIcon from "../../images/playIcon.svg";

export const AthleteStaticStrengthWorkoutExercise = () => {
  const navigate = useNavigate();
  const { athlete } = useAthleteAuthContext();
  const { id, type } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workoutData, setWorkoutData] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [oneRepMax, setOneRepMax] = useState(100);
  const [sets, setSets] = useState([]);
  const [originalSets, setOriginalSets] = useState([]);


  const convertToEmbedUrl = (url) => {
    if (!url) return '';
    const videoIdMatch = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})/);
    return videoIdMatch ? `https://www.youtube.com/embed/${videoIdMatch[1]}` : url;
  };

  useEffect(() => {
    const fetchWorkoutData = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_URL.GET_ATHLETE_STATIC_STRENGTH_WORKOUT_BY_ID + id + "/" + type, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${athlete.token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch workout data");
        }
        const data = await response.json();
        console.log(data);

        // Check if the data contains sets
        if (data.sets && Array.isArray(data.sets)) {
            setWorkoutData(data);
            setSets(data.sets); // Load sets data initially
            setOriginalSets(data.sets); // Store original sets for future updates/comparison
        } else {
            setSets([]);  // If no sets data is available, set empty
            setOriginalSets([]);  // Clear originalSets as well
        }
      
        if (data.video_url) {
          setEmbedUrl(convertToEmbedUrl(data.video_url));
        }
        
      } catch (error) {
        console.error("Error fetching workout data:", error);
        setError("Failed to load workout");
      } finally {
        setLoading(false);
      }
    };
    fetchWorkoutData();
  }, [athlete.token, id, type]);

// When the workout data is fetched, check for 1RM in localStorage and update sets accordingly
useEffect(() => {
    if (workoutData && workoutData.sets) {
      // Only fetch 1RM once, from localStorage
      const storedOneRepMax = localStorage.getItem(`oneRepMax-${id}`);
      if (storedOneRepMax) {
        console.log("1RM from localStorage:", storedOneRepMax);  // Debugging log
        setOneRepMax(storedOneRepMax); // Set the 1RM from localStorage
      }
    }
  }, [workoutData, id]); // Run this only when workoutData is loaded


  // Call updateSetWeights when 1RM is set and workoutData is ready
useEffect(() => {
    if (oneRepMax && workoutData) {
      console.log("Calling updateSetWeights for 1RM:", oneRepMax);  // Debugging log
      updateSetWeights(oneRepMax);
    }
  }, [oneRepMax, workoutData]); // Trigger update only after 1RM and workoutData are set
  

  const changeDate = (days) => {
    setSelectedDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + days);
      return newDate;
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  };
  
  const updateSetWeights = (newOneRepMax) => {
    console.log("Updating set weights for 1RM:", newOneRepMax);  // Debugging log
  
    if (!newOneRepMax || isNaN(newOneRepMax) || newOneRepMax <= 0) {
      console.log("1RM value is invalid or empty, skipping update");
      return; // Exit if 1RM value is invalid
    }
  
    // Check if originalSets has data before proceeding
    console.log("Original sets before update:", originalSets);
  
    // Update sets' weights based on the 1RM
    const updatedSetsWithWeights = originalSets.map(set => {
      if (set.weight_unit === '%') {
        // Perform calculation only if weight_unit is '%'
        const percentageWeight = (set.weight * newOneRepMax) / 100;
        return {
          ...set,
          weight: Math.round(percentageWeight), // Round to the nearest integer
          weight_unit: 'lbs', // Convert unit to lbs
        };
      }
        // If weight_unit is not '%', keep the weight unchanged
        return set;
    });
  
    console.log("Updated sets with new weights:", updatedSetsWithWeights);
    setSets(updatedSetsWithWeights);  // Set the updated sets
  };

  const handleOneRepMaxChange = (event) => {
    const value = parseFloat(event.target.value);
    
    if (!isNaN(value) && value > 0) {
      setOneRepMax(value);
      localStorage.setItem(`oneRepMax-${id}`, value);
      updateSetWeights(value); // Update weights when 1RM is entered
    } else {
      setOneRepMax('');
      localStorage.setItem(`oneRepMax-${id}`, value);
      setSets(originalSets); // Restore original sets data if 1RM is cleared
    }
  };

   // Handle back button click and pass templates state back
   const handleBackClick = () => {
    navigate(-1); // Pass templates back on go back
  };

  const renderExerciseDetails = (exercise, index) => (
    <div key={exercise._id || index} className="bg-black rounded-lg p-4 mb-4">
      <div className="relative h-[210px]">
        <img
          src={exercise.video_thumbnail || strengthLarge}
          alt={exercise.title}
          className="h-full w-full object-cover"
        />
        {exercise.video_thumbnail && (
          <img
            src={playIcon}
            alt="Play Video"
            className="object-contain absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 cursor-pointer"
            onClick={() => {
              setShowVideo(true);
              setEmbedUrl(convertToEmbedUrl(exercise.video_url));
            }}
          />
        )}
        <div className="absolute bottom-4 left-4">
          <p className="text-white text-xl font-normal uppercase">{exercise.title}</p>
        </div>
      </div>
      <p className="text-white mt-2">{exercise.instructions}</p>
    </div>
  );

  const renderSupersetOrCircuit = (group, index) => (
    <div key={index} className="bg-black rounded-lg p-4 mb-4">
      <div className="relative h-[210px]">
        <img
          src={strengthLarge}
          alt={group.type}
          className="h-full w-full object-cover"
        />
        <div className="absolute bottom-4 left-4">
          <p className="text-white text-xl font-normal uppercase">{group.type === 'superset' ? 'Superset' : 'Circuit'}</p>
        </div>
      </div>
      {/* Group all exercises inside the Superset or Circuit */}
      <div className="bg-black rounded-t-[20px] relative z-10 -mt-5 pt-5 px-5">
        <div className="py-5 mt-5 flex flex-col gap-[10px] border-y border-white/20">
          {group.exercises?.map((subExercise, subIndex) => renderExerciseDetails(subExercise, subIndex))}
        </div>
        <div className="py-5 border-b border-white/20">
          <p className="text-white text-sm font-normal">{group.notes}</p>
        </div>
        <div className="py-5">
          <p className="text-white text-sm font-normal">{group.rest_details}</p>
        </div>
      </div>
    </div>
  );


  return (
    <AthleteLayout>
      <div className="bg-white">
        <AthleteHeader />
      </div>
      <div className="pb-3 pt-2 flex flex-row justify-center items-center gap-10 bg-white">
        <ArrowL />
            <p className="text-black text-xl font-normal">{formatDate(selectedDate)}</p>
        <ArrowG />
      </div>

     

      {loading && (
          <div className="text-center py-10">
            <p className="text-white text-lg">Loading...</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="text-center py-10">
            <p className="text-red-500 text-lg">{error}</p>
          </div>
        )}
       
       {!loading && !error && workoutData && (
      <>
     

      <div className="h-full w-full max-w-2xl mx-auto ">
     
      <div className="w-full text-center relative py-4">
            <span
              className="absolute left-5 top-1/2 -translate-y-1/2 flex items-center gap-2 cursor-pointer text-white hover:text-gray-300"
              onClick={handleBackClick}
            >
              <BackArrow /> Back
            </span>
        </div>

        {Array.isArray(workoutData.exercises)
          ? workoutData.exercises.map((exercise, index) => {
              if (exercise.type === 'superset' || exercise.type === 'circuit') {
                return renderSupersetOrCircuit(exercise, index);
              } else {
                return renderExerciseDetails(exercise, index);
              }
            })
          : renderExerciseDetails(workoutData)}

         <div className="p-4 mb-4 py-5 mt-5 flex flex-col gap-[10px] border-y border-white/20">
         {sets.map((set, index) => (
                <div key={index} className="flex justify-between gap-3">
                  <p className="text-white text-sm font-normal">Set {index + 1}</p>
                  <p className="text-white text-sm font-normal">
                  {set.weight_unit === "Hide" ? (
                    <>
                      {set.reps} (reps) - {set.goal} (goal)
                    </>
                  ) : (
                    <>
                      {set.reps} (reps) x {set.weight} lbs (weight) - {set.goal} (goal)
                    </>
                  )}

                  
                  </p>
                </div>
            ))}
          </div>
          <div className="p-4 mb-4 py-5 border-b border-white/20">
                <p className="text-white text-sm font-normal">
                {workoutData.notes}
                </p>
            </div>
            <div className="p-4 mb-4 py-5">
                <p className="text-white text-sm font-normal">
                {workoutData.rest_details}
                </p>
            </div>

             {/* Input for One Rep Max */}
            <div className="p-4 mb-4 py-5">
            <label className="text-white text-sm font-normal">One Rep Max (1RM)</label>
            <input
                type="number"
                value={oneRepMax}
                onChange={handleOneRepMaxChange}
                className="mt-2 w-full bg-black text-white rounded"
                placeholder="Enter your One Rep Max"
            />
            </div>

        </div>
     

      {showVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="relative w-[90%] max-w-lg bg-black">
            <button
              className="absolute top-2 right-2 text-white"
              onClick={() => setShowVideo(false)}
            >
              Close
            </button>
            <iframe
              width="100%"
              height="315"
              src={embedUrl}
              title="Workout Video"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
       
      </>
       )};



    </AthleteLayout>
  );
};

const BackArrow = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        stroke="white"
        d="M15 19l-7-7 7-7"
      />
    </svg>
  );
};

const ArrowG = ({ onClick }) => (
  <svg
    onClick={onClick}
    className="hover:opacity-50 cursor-pointer"
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="14"
    viewBox="0 0 8 14"
    fill="none"
  >
    <path d="M1 13L7 7L1 1" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowL = ({ onClick }) => (
  <svg
    onClick={onClick}
    className="hover:opacity-50 rotate-180 cursor-pointer"
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="14"
    viewBox="0 0 8 14"
    fill="none"
  >
    <path d="M1 13L7 7L1 1" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
